// The focus section that took the yellow Play hero's place — the two things
// the dashboard should put in front of you before anything else.
//
// What those two are depends on the persona:
//   • CSOC     — Class and Assignment. Class detail used to be a carousel
//                slide, which meant waiting for it to rotate round before you
//                could see when your class was; it's a fixed card now.
//   • non-CSOC — no class and no assignment, so Play and Fun Challenge are
//                promoted here from the shortcut grid below rather than
//                leaving a half-width card with a hole beside it. They render
//                out of QuickActions so the two placements stay one component.

import React, {useEffect, useRef} from 'react';
import {Animated, Image, Pressable, StyleSheet, View} from 'react-native';
import {LinearGradient} from 'expo-linear-gradient';
import Icon from 'react-native-vector-icons/FontAwesome5';
import CCText from '@/Atoms/CCText';
import CCColors from '@/Utils/CCColors';
import RadarDot from '@/Atoms/RadarDot';
import NudgeArrow from '@/Atoms/NudgeArrow';
import {ScreenName} from '@/navigators/StackConstants';
import {hoverLift, pressSink, transition} from '@/Utils/motion';
import {convertISTDateTimeToLocal} from '@/Utils/CommonUtils';
import {formatNextClassLabel} from '../../personaHelpers';
import {FunChallengeCard, PlayCard} from '../QuickActions';
import {radii} from '@/Utils/radius';

interface FocusCardsProps {
  navigation: any;
  isCsoc: boolean;
  classes?: {today: any; next: any};
  /** Column layout at narrow widths. */
  stacked?: boolean;
  /** Viewer's profile-selected timezone (user.user_timezone.selected), e.g.
   * "America/New_York (GMT-04:00)" -- same source Meetings.tsx reads. */
  userTimezone?: string | null;
  /** Subscription expiry info — when present and daysToExpiry <= 14 a warning
   * strip appears above the class card with a Renew button. */
  renewalWarning?: {
    daysToExpiry: number;
    sessionsLeft: number;
    onRenew: () => void;
  };
}

interface CardShellProps {
  /** Saturated card background — the Play hero's treatment, not a pale tint. */
  bg: string;
  /** Text/rail colour that reads on `bg`. */
  ink: string;
  icon: string;
  kicker: string;
  live?: boolean;
  title: string;
  sub: string;
  cta: string;
  onPress: () => void;
  stacked?: boolean;
  /** When set, a warning footer is rendered inside the card bottom. */
  renewalWarn?: {text: string; onRenew: () => void};
}

function FocusCard({
  bg,
  ink,
  icon,
  kicker,
  live,
  title,
  sub,
  cta,
  onPress,
  stacked,
  renewalWarn,
}: CardShellProps) {
  // The card's own width, not the viewport's: two of these share a row on
  // desktop and go full-bleed on a phone, so only the element knows how much
  // room it has.
  const [width, setWidth] = React.useState(0);
  const narrow = width > 0 && width < 400;

  const blink = useRef(new Animated.Value(1)).current;
  useEffect(() => {
    if (!renewalWarn) return;
    const anim = Animated.loop(
      Animated.sequence([
        Animated.timing(blink, {toValue: 0.35, duration: 700, useNativeDriver: true}),
        Animated.timing(blink, {toValue: 1, duration: 700, useNativeDriver: true}),
      ]),
    );
    anim.start();
    return () => anim.stop();
  }, [renewalWarn, blink]);

  return (
    <Pressable
      onPress={onPress}
      onLayout={e => setWidth(e.nativeEvent.layout.width)}
      style={({hovered, pressed}: any) => [
        styles.card,
        {backgroundColor: bg, cursor: 'pointer'},
        {
          flexGrow: stacked ? 0 : 1,
          flexBasis: stacked ? 'auto' : '48%',
        },
        hovered && styles.cardHovered,
        pressed && styles.cardPressed,
      ]}>
      {({hovered}: any) => (
        <>
          {/* Sheen across the tint, lightest at the top-left — the same
              treatment the trainer tiles use, so the two sections read as one
              family. */}
          <LinearGradient
            colors={['rgba(255,255,255,0.35)', 'rgba(255,255,255,0)']}
            start={{x: 0, y: 0}}
            end={{x: 0.9, y: 1}}
            style={StyleSheet.absoluteFill}
            pointerEvents="none"
          />

          {/* Chess-piece watermark, same treatment the Play hero used: white
              pieces at low opacity bleeding off the bottom-right. Rendered
              before the body so the copy and button paint over it, and the
              card's overflow:hidden clips it to the rounded corners. */}
          <View style={styles.art} pointerEvents="none">
            <Image
              source={require('@/Assets/ChessPieces/White-Knight.svg')}
              style={styles.artPiece}
              resizeMode="contain"
            />
            <Image
              source={require('@/Assets/ChessPieces/White-Pawn.svg')}
              style={styles.artPieceSmall}
              resizeMode="contain"
            />
          </View>

          {/* Icon | copy | CTA across the card's width. Stacked vertically the
              copy only occupied the left third and the whole right side of a
              wide card sat empty; this spends the width instead of leaving it
              blank. Falls back to a column when the card is genuinely narrow. */}
          <View style={[styles.body, narrow && styles.bodyNarrow]}>
            <View
              style={[
                styles.iconChip,
                hovered && {transform: [{scale: 1.05}]},
              ]}>
              <Icon name={icon} size={17} color={ink} />
            </View>

            <View style={styles.copy}>
              <View style={styles.kickerRow}>
                {live ? <RadarDot color={ink} size={7} /> : null}
                <CCText fontSize={10.5} fontWeight="bold" style={{color: ink}}>
                  {kicker}
                </CCText>
              </View>
              <CCText
                fontSize={16}
                fontWeight="bold"
                numberOfLines={2}
                style={[styles.title, {color: ink}]}>
                {title}
              </CCText>
              <CCText
                fontSize={12.5}
                numberOfLines={2}
                style={[styles.sub, {color: ink, opacity: 0.75}]}>
                {sub}
              </CCText>
            </View>

            {/* A pill that fills with the card's accent on hover, rather than a
                bare line of coloured text that didn't read as a control. */}
            <View
              style={[
                styles.ctaPill,
                narrow && styles.ctaPillNarrow,
                hovered && {backgroundColor: ink},
              ]}>
              <CCText
                fontSize={13}
                fontWeight="bold"
                style={{color: hovered ? CCColors.White : ink}}>
                {cta}
              </CCText>
              <NudgeArrow
                hovered={hovered}
                fontSize={14}
                color={hovered ? CCColors.White : ink}
              />
            </View>
          </View>

          {renewalWarn && (
            <Animated.View style={[styles.renewalBadgeWrap, {opacity: blink}]}>
              <Pressable
                onPress={e => {
                  e.stopPropagation?.();
                  renewalWarn.onRenew();
                }}
                style={({hovered: h}: any) => [
                  styles.renewalBadge,
                  h && styles.renewalBadgeHovered,
                ]}>
                {() => (
                  <>
                    <Icon name="exclamation-circle" size={10} color={CCColors.White} />
                    <CCText fontSize={10} fontWeight="bold" style={{color: CCColors.White}}>
                      {renewalWarn.text}
                    </CCText>
                  </>
                )}
              </Pressable>
            </Animated.View>
          )}
        </>
      )}
    </Pressable>
  );
}

export default function FocusCards({
  navigation,
  isCsoc,
  classes,
  stacked,
  userTimezone,
  renewalWarning,
}: FocusCardsProps) {
  const today = classes?.today;
  const next = classes?.next;
  // Same conversion Meetings.tsx does inline: webinar_time is a raw IST
  // string, correct only for India, so it's run through
  // convertISTDateTimeToLocal with the viewer's profile-selected timezone
  // before display.
  const todayLocalTime =
    today?.webinar_date && today?.webinar_time
      ? convertISTDateTimeToLocal(
          today.webinar_date,
          today.webinar_time,
          userTimezone,
        )?.localTimeRange ?? ''
      : today?.webinar_time || '';

  const activeClass = today || next;
  const sessionNum: number | undefined = activeClass?.session_number;
  const sessionPrefix = sessionNum != null ? `Session ${sessionNum} · ` : '';

  const showRenewalWarn =
    isCsoc && renewalWarning != null && renewalWarning.daysToExpiry <= 14;
  const renewalWarnText = renewalWarning
    ? renewalWarning.sessionsLeft <= 0
      ? 'No classes left · Renew'
      : renewalWarning.sessionsLeft === 1
      ? '1 class left · Renew'
      : `${renewalWarning.sessionsLeft} classes left · Renew`
    : '';

  return (
    // A card, not a bare row: these sat straight on the page background, so two
    // tinted panels read as loose decoration rather than a section. No heading
    // — each card already states what it is in its own kicker.
    <View style={styles.section}>
      <View style={[styles.row, stacked && styles.rowStacked]}>
        {isCsoc ? (
          <>
            <FocusCard
              stacked={stacked}
              bg="#F2A08A"
              ink="#5C2416"
              icon="chalkboard-teacher"
              kicker={
                today
                  ? `${sessionPrefix}CLASS TODAY`
                  : next
                  ? `${sessionPrefix}NEXT CLASS`
                  : 'YOUR CLASSES'
              }
              live={!!today}
              title={
                today
                  ? `${todayLocalTime} · ${today.topic}`
                  : next
                  ? formatNextClassLabel(next, userTimezone)
                  : 'No class scheduled yet'
              }
              sub={
                today
                  ? 'Your live class is today'
                  : next
                  ? next.topic || 'Your next live class'
                  : 'Your coach will schedule your next session'
              }
              cta={today ? 'Join class' : 'View schedule'}
              renewalWarn={
                showRenewalWarn
                  ? {text: renewalWarnText, onRenew: renewalWarning!.onRenew}
                  : undefined
              }
              // Meetings tab rather than the raw Zoom link: that screen has the
              // join/reschedule state and popup-blocker handling.
              onPress={() =>
                navigation.navigate(ScreenName.Books, {tab: 'meeting'})
              }
            />
            <FocusCard
              stacked={stacked}
              bg="#FACF47"
              ink="#4D3F37"
              icon="tasks"
              kicker="ASSIGNMENTS"
              title="Your assignments"
              sub="Homework and practice sets from your coach"
              cta="Open assignments"
              onPress={() =>
                navigation.navigate(ScreenName.Books, {tab: 'user_assignment'})
              }
            />
          </>
        ) : (
          <>
            <PlayCard navigation={navigation} variant="hero" stacked={stacked} />
            <FunChallengeCard
              navigation={navigation}
              variant="hero"
              stacked={stacked}
            />
          </>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  section: {
    backgroundColor: CCColors.White,
    borderRadius: radii.panel,
    padding: 18,
    shadowColor: CCColors.Primary.Dark,
    shadowRadius: 8,
    shadowOffset: {width: 0, height: 4},
    shadowOpacity: 0.05,
    // Above the shortcut grid below it, so the Play card's time-control menu
    // opens over that section instead of being painted under it.
    zIndex: 2,
  },
  renewalBadgeWrap: {
    position: 'absolute',
    top: 10,
    right: 10,
  },
  renewalBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: '#DC2626',
    paddingHorizontal: 9,
    paddingVertical: 5,
    borderRadius: 99,
    cursor: 'pointer' as any,
    shadowColor: '#DC2626',
    shadowOpacity: 0.45,
    shadowRadius: 6,
    shadowOffset: {width: 0, height: 2},
    ...transition(150),
  },
  renewalBadgeHovered: {
    backgroundColor: '#B91C1C',
    shadowOpacity: 0.6,
  },
  row: {
    flexDirection: 'row',
    // Stretch, not wrap: the two cards differ in content height (one carries a
    // dropdown and buttons) and a wrapping row lets each size independently,
    // which left them ragged. Equal height keeps their footers on one line.
    alignItems: 'stretch',
    gap: 10,
  },
  rowStacked: {
    flexDirection: 'column',
  },
  card: {
    flexGrow: 1,
    minWidth: 0,
    borderRadius: radii.panel,
    paddingLeft: 16,
    paddingRight: 14,
    paddingVertical: 16,
    position: 'relative',
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.7)',
    shadowColor: '#4D3F37',
    shadowOpacity: 0.06,
    shadowRadius: 3,
    shadowOffset: {width: 0, height: 2},
    ...transition(170),
  },
  art: {
    position: 'absolute',
    right: 0,
    top: 0,
    bottom: 0,
    width: '48%',
  },
  artPiece: {
    position: 'absolute',
    right: 6,
    bottom: -30,
    width: 124,
    height: 124,
    opacity: 0.22,
    transform: [{rotate: '-8deg'}],
  },
  artPieceSmall: {
    position: 'absolute',
    right: 108,
    bottom: -18,
    width: 86,
    height: 86,
    opacity: 0.14,
    transform: [{rotate: '12deg'}],
  },
  cardHovered: hoverLift(4, 1.01),
  cardPressed: pressSink(0.99),
  body: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 13,
  },
  bodyNarrow: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 10,
  },
  copy: {
    flex: 1,
    minWidth: 0,
  },
  iconChip: {
    width: 38,
    height: 38,
    borderRadius: radii.chip,
    backgroundColor: CCColors.White,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    shadowColor: '#4D3F37',
    shadowOpacity: 0.08,
    shadowRadius: 4,
    shadowOffset: {width: 0, height: 2},
    ...transition(170),
  },
  kickerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flexShrink: 1,
    minWidth: 0,
  },
  title: {
    color: CCColors.TextColor.Dark,
    marginTop: 4,
  },
  sub: {
    marginTop: 3,
  },
  ctaPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    // flexShrink 0 so the copy column absorbs the slack instead of the button
    // being squeezed; that slack is what used to sit empty on the right.
    flexShrink: 0,
    paddingHorizontal: 15,
    paddingVertical: 8,
    borderRadius: 99,
    // Solid white on a saturated card, like the Play hero's inner card.
    backgroundColor: CCColors.White,
    shadowColor: '#4D3F37',
    shadowOpacity: 0.12,
    shadowRadius: 5,
    shadowOffset: {width: 0, height: 2},
    ...transition(170),
  },
  ctaPillNarrow: {
    // Column layout puts the icon on its own row, so the CTA starts flush left
    // with everything else rather than indented under the copy.
    alignSelf: 'flex-start',
    marginTop: 2,
  },
});
